import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  Visibility,
  Pause,
  PlayArrow,
  Block,
  TrendingUp,
  AttachMoney,
  Schedule,
  Star,
  Close,
  Cancel,
} from '@mui/icons-material';
import ServiceProviderDetail from './ServiceProviderDetail';

interface ServiceProvider {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  services: string[];
  status: 'active' | 'paused' | 'deactivated' | 'rejected';
  language?: 'en' | 'de';
  // Languages the provider speaks with customers (native-name display strings)
  spokenLanguages?: string[];
  rating: number;
  totalBookings: number;
  totalEarnings: number;
  responseTime: number; // in hours
  lastContacted: string | null;
  joinDate: string;
  applicationDate?: string;
  rejectionDate?: string;
  rejectionReason?: string;
  rejectedBy?: string;
  location: {
    city: string;
    state: string;
  };
  completionRate: number;
  isFoundingMember?: boolean;
}

interface ServiceProviderListProps {
  onClose: () => void;
}

const ServiceProviderList: React.FC<ServiceProviderListProps> = ({ onClose }) => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);
  const [showProviderDetail, setShowProviderDetail] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchServiceProviders();
  }, []);

  const fetchServiceProviders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/service-providers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      } else {
        throw new Error('Failed to fetch service providers');
      }
    } catch (error) {
      console.error('Error fetching service providers:', error);
      setError('Failed to load service providers. Please try again.');
      // Mock data for development
      setProviders([
        {
          id: '1',
          name: 'Sarah Johnson',
          email: 'sarah.johnson@email.com',
          phone: '+1 (555) 123-4567',
          services: ['Dog Walking', 'Pet Boarding', 'Dog Daycare'],
          status: 'active',
          rating: 4.8,
          totalBookings: 156,
          totalEarnings: 4680,
          responseTime: 2.5,
          lastContacted: '2025-01-15T10:30:00Z',
          joinDate: '2024-06-15T00:00:00Z',
          location: { city: 'San Francisco', state: 'CA' },
          completionRate: 98,
        },
        {
          id: '2',
          name: 'Mike Rodriguez',
          email: 'mike.rodriguez@email.com',
          phone: '+1 (555) 987-6543',
          services: ['Pet Boarding', 'House Visits'],
          status: 'paused',
          rating: 4.6,
          totalBookings: 89,
          totalEarnings: 2670,
          responseTime: 4.2,
          lastContacted: '2025-01-10T14:20:00Z',
          joinDate: '2024-08-22T00:00:00Z',
          location: { city: 'Los Angeles', state: 'CA' },
          completionRate: 95,
        },
        {
          id: '3',
          name: 'Emily Chen',
          email: 'emily.chen@email.com',
          phone: '+1 (555) 456-7890',
          services: ['Dog Walking', 'Pet Sitting'],
          status: 'active',
          rating: 4.9,
          totalBookings: 203,
          totalEarnings: 6090,
          responseTime: 1.8,
          lastContacted: '2025-01-20T09:15:00Z',
          joinDate: '2024-05-10T00:00:00Z',
          location: { city: 'New York', state: 'NY' },
          completionRate: 99,
        },
        {
          id: '4',
          name: 'David Thompson',
          email: 'david.thompson@email.com',
          phone: '+1 (555) 321-0987',
          services: ['Pet Boarding'],
          status: 'rejected',
          rating: 0,
          totalBookings: 0,
          totalEarnings: 0,
          responseTime: 0,
          lastContacted: null,
          applicationDate: '2024-12-15T00:00:00Z',
          rejectionDate: '2024-12-18T00:00:00Z',
          rejectionReason: 'Insufficient experience with pet care. No references provided.',
          rejectedBy: 'Admin User',
          joinDate: '2024-12-15T00:00:00Z',
          location: { city: 'Chicago', state: 'IL' },
          completionRate: 0,
        },
        {
          id: '5',
          name: 'Jessica Martinez',
          email: 'jessica.martinez@email.com',
          phone: '+1 (555) 789-4567',
          services: ['Dog Walking', 'Pet Sitting', 'House Visits'],
          status: 'rejected',
          rating: 0,
          totalBookings: 0,
          totalEarnings: 0,
          responseTime: 0,
          lastContacted: null,
          applicationDate: '2024-11-20T00:00:00Z',
          rejectionDate: '2024-11-22T00:00:00Z',
          rejectionReason: 'Background check did not meet our safety standards.',
          rejectedBy: 'Sarah Admin',
          joinDate: '2024-11-20T00:00:00Z',
          location: { city: 'Miami', state: 'FL' },
          completionRate: 0,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLElement>, provider: ServiceProvider) => {
    event.stopPropagation();
    setSelectedProvider(provider);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedProvider(null);
  };

  const handleViewDetails = () => {
    if (selectedProvider) {
      setShowProviderDetail(true);
    }
    handleActionMenuClose();
  };

  const handleToggleStatus = async (status: 'active' | 'paused' | 'deactivated') => {
    if (!selectedProvider) return;

    try {
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/service-providers/${selectedProvider.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (response.ok) {
        setSuccess(`Service provider ${status === 'active' ? 'activated' : status === 'paused' ? 'paused' : 'deactivated'} successfully`);
        fetchServiceProviders();
      } else {
        throw new Error('Failed to update service provider status');
      }
    } catch (error) {
      setError('Failed to update service provider status. Please try again.');
    }
    handleActionMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'deactivated': return 'error';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <PlayArrow />;
      case 'paused': return <Pause />;
      case 'deactivated': return <Block />;
      case 'rejected': return <Cancel />;
      default: return undefined;
    }
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.services.some(service => service.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Summary statistics
  const totalProviders = providers.length;
  const activeProviders = providers.filter(p => p.status === 'active').length;
  const pausedProviders = providers.filter(p => p.status === 'paused').length;
  const rejectedProviders = providers.filter(p => p.status === 'rejected').length;
  const totalRevenue = providers.reduce((sum, p) => sum + p.totalEarnings, 0);

  return (
    <Box sx={{ width: '95vw', maxWidth: 1400, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Service Provider Management
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Providers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {totalProviders}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Providers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {activeProviders}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <PlayArrow />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Paused/Inactive
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {pausedProviders}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Pause />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Rejected
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {rejectedProviders}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <Cancel />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AttachMoney />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Status Filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="deactivated">Deactivated</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Providers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Provider</strong></TableCell>
              <TableCell><strong>Services</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Languages</strong></TableCell>
              <TableCell><strong>Rating</strong></TableCell>
              <TableCell><strong>Bookings</strong></TableCell>
              <TableCell><strong>Earnings</strong></TableCell>
              <TableCell><strong>Response Time</strong></TableCell>
              <TableCell><strong>Last Contact</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  Loading service providers...
                </TableCell>
              </TableRow>
            ) : filteredProviders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  {searchTerm || statusFilter !== 'all' ? 'No providers match your filters' : 'No service providers found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredProviders.map((provider) => (
                <TableRow key={provider.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar src={provider.avatar} sx={{ bgcolor: 'primary.main' }}>
                        {provider.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {provider.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {provider.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {provider.location.city}, {provider.location.state}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {provider.services.slice(0, 2).map((service, index) => (
                        <Chip key={index} label={service} size="small" variant="outlined" />
                      ))}
                      {provider.services.length > 2 && (
                        <Chip label={`+${provider.services.length - 2} more`} size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={provider.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(provider.status)}
                      icon={getStatusIcon(provider.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={provider.language === 'de' ? 'Deutsch' : 'English'} color={provider.language === 'de' ? 'info' : 'default'} />
                    {provider.spokenLanguages && provider.spokenLanguages.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Speaks: {provider.spokenLanguages.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ color: '#ffd700', fontSize: 16 }} />
                      <Typography variant="body2" fontWeight="medium">
                        {provider.rating.toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {provider.status === 'rejected' ? (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" fontWeight="medium">
                          {provider.totalBookings}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {provider.completionRate}% completion
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {provider.status === 'rejected' ? (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(provider.totalEarnings)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {provider.status === 'rejected' ? (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        {provider.responseTime.toFixed(1)}h
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {provider.status === 'rejected' ? (
                      <Box>
                        <Typography variant="body2" color="error.main" fontWeight="medium">
                          Rejected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(provider.rejectionDate!)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        {formatDate(provider.lastContacted)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="More actions">
                      <IconButton
                        onClick={(e) => handleActionMenuClick(e, provider)}
                        size="small"
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        {selectedProvider && (
          <>
            <MenuItem onClick={handleViewDetails}>
              <Visibility sx={{ mr: 1 }} />
              View Details & Analytics
            </MenuItem>
            {selectedProvider.status !== 'active' && (
              <MenuItem onClick={() => handleToggleStatus('active')} sx={{ color: 'success.main' }}>
                <PlayArrow sx={{ mr: 1 }} />
                Activate Services
              </MenuItem>
            )}
            {selectedProvider.status === 'active' && (
              <MenuItem onClick={() => handleToggleStatus('paused')} sx={{ color: 'warning.main' }}>
                <Pause sx={{ mr: 1 }} />
                Pause Services
              </MenuItem>
            )}
            <MenuItem 
              onClick={() => handleToggleStatus('deactivated')}
              sx={{ color: 'error.main' }}
            >
              <Block sx={{ mr: 1 }} />
              Deactivate Provider
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Service Provider Detail Modal */}
      {showProviderDetail && selectedProvider && (
        <ServiceProviderDetail
          provider={selectedProvider}
          open={showProviderDetail}
          onClose={() => {
            setShowProviderDetail(false);
            setSelectedProvider(null);
          }}
          onUpdate={fetchServiceProviders}
        />
      )}
    </Box>
  );
};

export default ServiceProviderList;